import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Address } from 'viem'
import { useETransferDialog } from './logic/useETransferDialog'
import { ETransferView } from './views/ETransferView'
import { withSuspense } from '@/ui/utils/withSuspense'
import { DialogContentSkeleton } from '@/features/dialogs/common/components/skeletons/DialogContentSkeleton'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { SuccessViewContent } from '@/features/dialogs/common/components/success-view/SuccessViewContent'
import { SuccessViewProceedButton } from '@/features/dialogs/common/components/success-view/SuccessViewProceedButton'
import { SuccessViewSummaryPanel } from '@/features/dialogs/common/components/success-view/SuccessViewSummaryPanel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'

interface ETransferDialogContentContainerProps {
  closeDialog: () => void
  vaultAddress: Address
  underlyingAsset: Address
  assetSymbol: string
  assetDecimals: number
  logoURI?: string
  priceUSD: number
  currentBalance: number
  sourceSubAccountId: number
}

function ETransferDialogContentContainerInternal({
  closeDialog,
  vaultAddress,
  underlyingAsset,
  assetSymbol,
  assetDecimals,
  logoURI,
  priceUSD,
  currentBalance,
  sourceSubAccountId,
}: ETransferDialogContentContainerProps) {
  const {
    token,
    transferAmount,
    setTransferAmount,
    maxTransferable,
    selectedDestination,
    setSelectedDestination,
    destinations,
    isDestinationsLoading,
    transferValueUSD,
    sourceSubAccountLabel,
    canTransfer,
    isDebouncing,
    isSimulating,
    isWriting,
    isWaiting,
    isConfirmed,
    hasSimulationData,
    displayError,
    simulationError,
    handleExecute,
    isSuccess,
    isTimedOut,
    currentSourceHF,
    newSourceHF,
    isSimulatingSourceHF,
    sourceHasDebt,
  } = useETransferDialog({
    vaultAddress,
    underlyingAsset,
    assetSymbol,
    assetDecimals,
    logoURI,
    priceUSD,
    currentBalance,
    sourceSubAccountId,
  })

  if (isSuccess && token) {
    const value = NormalizedUnitNumber(transferAmount)

    return (
      <SuccessViewContent>
        <SuccessViewCheckmark />
        <SuccessViewSummaryPanel title="Transferred">
          <div className="flex w-full flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TokenIcon token={token} className="h-6" />
                <span className="font-semibold">{token.symbol}</span>
              </div>
              <div className="flex flex-col">
                <Typography className="text-right font-primary">
                  {token.format(value, { style: 'auto' })}
                </Typography>
                <Typography variant="prompt" className="text-right">
                  {token.formatUSD(value)}
                </Typography>
              </div>
            </div>
            {selectedDestination && (
              <div className="flex items-center justify-between border-border border-t pt-3">
                <span className="text-muted-foreground text-sm">Destination</span>
                <span className="font-semibold text-sm">{selectedDestination.label}</span>
              </div>
            )}
          </div>
        </SuccessViewSummaryPanel>
        <SuccessViewProceedButton onProceed={closeDialog}>Back to Position</SuccessViewProceedButton>
      </SuccessViewContent>
    )
  }

  return (
    <ETransferView
      assetSymbol={assetSymbol}
      logoURI={logoURI}
      transferAmount={transferAmount}
      setTransferAmount={setTransferAmount}
      maxTransferable={maxTransferable}
      selectedDestination={selectedDestination}
      setSelectedDestination={setSelectedDestination}
      destinations={destinations}
      isDestinationsLoading={isDestinationsLoading}
      sourceSubAccountLabel={sourceSubAccountLabel}
      transferValueUSD={transferValueUSD}
      token={token}
      isDebouncing={isDebouncing}
      isSimulating={isSimulating}
      isWriting={isWriting}
      isWaiting={isWaiting}
      isConfirmed={isConfirmed}
      hasSimulationData={hasSimulationData}
      canTransfer={canTransfer}
      displayError={displayError}
      simulationError={simulationError}
      onExecute={handleExecute}
      currentSourceHF={currentSourceHF}
      newSourceHF={newSourceHF}
      isSimulatingSourceHF={isSimulatingSourceHF}
      sourceHasDebt={sourceHasDebt}
      isTimedOut={isTimedOut}
    />
  )
}

export const ETransferDialogContentContainer = withSuspense(
  ETransferDialogContentContainerInternal,
  DialogContentSkeleton,
)
